<template>
    <b-container class="full-height messages-component" fluid>
        <b-row class="h-100">
            <b-col cols="3" class="h-100 overflow-auto bg-dark toolbox">
                <b-form class="inline-form pt-3">
                    <div class="inline-form-group">
                        <label for="deviceSelect">
                            {{$t('monitoring.messages.device')}}
                        </label>
                        <div class="inline-form-input">
                            <Loader v-if="deviceLoading" />
                            <b-form-select
                                v-if="!deviceLoading"
                                class="inline-form-input"
                                id="deviceSelect"
                                v-model="selectedDevice"
                                :dark="true"
                            >
                                <b-form-select-option v-for="item in deviceList" :key="item.id" :value="item.id">
                                    {{item.name}}
                                </b-form-select-option>
                            </b-form-select>
                        </div>
                    </div>
                    <div class="inline-form-group">
                        <label for="intervalSelect">
                            {{$t('monitoring.messages.interval')}}
                        </label>
                        <div class="inline-form-input">
                            <b-form-select
                                class="inline-form-input"
                                id="intervalSelect"
                                :dark="true"
                                v-model="selectedInterval"
                            >
                                <b-form-select-option :value="0">{{$t('monitoring.messages.interval.selected_period')}}</b-form-select-option>
                                <b-form-select-option :value="1">{{$t('monitoring.messages.interval.from_date_to_now')}}</b-form-select-option>
                            </b-form-select>
                        </div>
                    </div>
                    <div class="inline-form-group">
                        <label for="dateFrom">
                            {{$t('monitoring.messages.date_from')}}
                        </label>
                        <div class="inline-form-input">
                            <b-form-datepicker
                                class="form-datepicker"
                                id="dateFrom"
                                v-model="startDate"
                                :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
                                :dark="true"
                            />
                            <b-form-timepicker
                                v-model="startTime"
                                button-variant="dark"
                                :dark="true"
                            />
                        </div>
                    </div>

                    <div class="inline-form-group" v-show="finishDateEnabled()">
                        <label for="dateTo">
                            {{$t('monitoring.messages.date_to')}}
                        </label>
                        <div class="inline-form-input">
                            <b-form-datepicker
                                class="form-datepicker"
                                id="dateTo"
                                v-model="endDate"
                                :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
                                :dark="true"
                            />
                            <b-form-timepicker
                                v-model="endTime"
                                button-variant="dark"
                                :dark="true"
                            />
                        </div>
                    </div>
                    <div class="inline-form-group">
                        <div class="inline-form-input justify-content-end">
                            <b-button
                                variant="secondary"
                                @click="resetForm"
                            >{{$t('monitoring.messages.reset')}}</b-button>
                            <b-button
                                variant="primary"
                                :disabled="!selectedDevice || tableDataLoading"
                                @click="loadData"
                            >{{$t('monitoring.messages.search')}}</b-button>
                        </div>
                    </div>
                </b-form>
                <b-alert variant="danger" :show="trackLoadError">{{$t('track_load_error')}}</b-alert>

                <div>
                    <h6 class="header-bar">
                        {{$t('monitoring.messages.result')}}
                    </h6>
                    <b>{{$t('monitoring.messages.statistics')}}</b>
                    <b-row>
                        <b-col>{{$t('monitoring.messages.total_messages')}}</b-col>
                        <b-col>{{formatValue(deviceHistorySize)}}</b-col>
                    </b-row>
                    <b-row>
                        <b-col>{{$t('monitoring.messages.total_time')}}</b-col>
                        <b-col>{{formatValue(totalTime)}}</b-col>
                    </b-row>
                    <b-row>
                        <b-col>{{$t('monitoring.messages.total_distance')}}</b-col>
                        <b-col>{{formatValue(totalDistance)}}</b-col>
                    </b-row>
                    <b-row>
                        <b-col>{{$t('monitoring.messages.avg_speed')}}</b-col>
                        <b-col>{{formatValue(avgSpeed)}}</b-col>
                    </b-row>
                    <b-row>
                        <b-col>{{$t('monitoring.messages.max_speed')}}</b-col>
                        <b-col>{{formatValue(maxSpeed)}}</b-col>
                    </b-row>
                </div>
            </b-col>
            <b-col cols="9">
                <div class="map-container h-50">
                    <l-map
                        :zoom="map.zoom"
                        :center="map.center"
                        :options="map.mapOptions"
                        :bounds="map.bounds"
                        style="height: 100%"
                        @update:center="centerUpdate"
                        @update:zoom="zoomUpdate"
                        ref="map"
                    >
                        <l-tile-layer
                            :url="map.url"
                            :attribution="map.attribution"
                        />
                        <l-polyline
                            :lat-lngs="devicePath"
                            :color="pathColor"
                            :weight="2"
                        />
                    </l-map>
                </div>
                <div class="h-50 overflow-auto bg-dark data-container">
                    <h5 class="p-3" v-if="!selectedDevice">{{$t('monitoring.messages.no_device_selected')}}</h5>
                    <h5 class="p-3" v-if="anyTableData()">{{$t('monitoring.messages.no_data')}}</h5>
                    <Protector v-if="tableDataLoading" />
                    <div class="overflow-auto" v-if="!tableDataLoading && tableData.length">
                        <b-navbar class="table-toolbar" type="dark" variant="dark">
                            <b-form-group
                                :label="$t('monitoring.messages.per_page')"
                                label-for="perPageSelect"
                                class="mb-3 mr-2"
                            >
                                <b-form-select
                                    id="perPageSelect"
                                    class="per-page-select"
                                    :options="perPageOptions"
                                    v-model="perPage"
                                />
                            </b-form-group>
                            <b-pagination
                                v-model="currentPage"
                                :total-rows="tableData.length"
                                :per-page="perPage"
                                aria-controls="data-table"
                            />
                        </b-navbar>
                        <div class="messages-table">
                            <b-table
                                id="data-table"
                                sticky-header
                                striped
                                hover
                                table-variant="dark"
                                small
                                :items="tableData"
                                :fields="fields"
                                :per-page="perPage"
                                :current-page="currentPage"
                            />
                        </div>
                    </div>

                </div>

            </b-col>
        </b-row>
    </b-container>
</template>

<script lang="ts">
import Component from "vue-class-component";
import Vue from "vue";
import {hasValidCoordinates} from "../../../utils/coordinates";
import DeviceRepository from "../../../Repository/Device/DeviceRepository";
import {IDevicePacketsDto} from "../../../Repository/Device/Dto/IDevicePacketsDto";
import Protector from "../../Common/Protector.vue";
import {LMap, LMarker, LPolyline, LTileLayer} from "vue2-leaflet";
import {LatLng, LatLngBounds} from "leaflet";
import Loader from "../../Common/Loader.vue";
import DeviceGroupRepository from "../../../Repository/DeviceGroup/DeviceGroupRepository";
import {IDeviceDto} from "../../../Repository/DeviceGroup/Dto/IDeviceDto";

enum Interval {
    SELECTED_PERIOD = 0,
    FROM_DATE_TO_NOW = 1,
}

interface IMap {
    url: string,
    attribution: string,
    zoom: number,
    center: LatLng,
    bounds: LatLngBounds | null,
    mapOptions: any,
}

const MessagesProps = Vue.extend({
    props: {
        selectedDeviceProp: { required: false }
    },
})


@Component({
    components: { Protector, LMap, LTileLayer, LMarker, LPolyline, Loader }
})
class Messages extends MessagesProps {

    map: IMap = {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '',
        zoom: 11,
        center: new LatLng(55.750531, 37.621635),
        mapOptions: {},
        bounds: null,
    };

    selectedInterval: Interval = Interval.SELECTED_PERIOD;
    deviceHistory: IDevicePacketsDto | null = null;
    startDate: Date|string = new Date();
    startTime: string = '00:00:00';
    endDate: Date|string = new Date();
    endTime: string = '23:59:59';
    tableData: any[] = [];
    fields: any[] = [
        {
            order: 0,
            visible: true,
            label: '#',
            key: '#',
        },
        {
            order: 1,
            visible: true,
            label: this.$t('monitoring.messages.field.date'),
            key: 'date',
        },
        {
            order: 2,
            visible: true,
            label: this.$t('monitoring.messages.field.speed'),
            key: 'speed',
        },
        {
            order: 3,
            visible: true,
            label: this.$t('monitoring.messages.field.location'),
            key: 'location',
        },
        {
            order: 4,
            visible: true,
            label: this.$t('monitoring.messages.field.altitude'),
            key: 'altitude',
        },
        {
            order: 5,
            visible: true,
            label: this.$t('monitoring.messages.field.params'),
            key: 'params',
            tdClass: 'params-cell',
        },
    ];
    currentPage: number = 1;
    perPage = 10;
    tableDataLoading: boolean = false;
    trackLoadError: boolean = false;

    deviceLoading: boolean = true;
    deviceList: IDeviceDto[] = [];
    selectedDevice: number | null = null;
    devicePath: LatLng[] = [];

    get paramDictionary(): any {
      return  {
          exclude: [
              "LNG", "LAT", "HEIGHT", "GPS_SAT", "GLONASS_SAT", "SPEED"
          ],
          translate: [
              {
                  source: "EXT_VOLT",
                  target: "pwr_ext",
              },
              {
                  source: "INT_VOLT",
                  target: "pwr_int",
              },
          ],
      };
    }

    get perPageOptions(): any[] {
        return [
            { value: 10, text: '10' },
            { value: 15, text: '15' },
            { value: 20, text: '20' },
            { value: 50, text: '50' },
            { value: 100, text: '100' },
        ]
    }

    get deviceHistorySize(): number | null {
        return this.deviceHistory?.records?.length ?? null;
    }

    get totalTime(): number | null {
        return null;
    }

    get totalDistance(): number | null {
        return null;
    }

    get avgSpeed(): number | null {
        return null;
    }

    get maxSpeed(): number | null {
        let max:  number | null = null;
        this.deviceHistory?.records?.forEach(q => {
            if (!max) {
                max = q.coordinates?.speed;
            } else {
                if (q.coordinates?.speed && q.coordinates.speed > max) {
                    max = q.coordinates.speed;
                }
            }
        });
        return max;
    }


    get pathColor(): string {
        return '#9f31b0';
    }

    centerUpdate(value: any): void {
        this.map.center = value;
    }

    zoomUpdate(value: number): void {
        this.map.zoom = value;
    }

    updateDeviceMessages(): void {
        const now = new Date();
        const endDate = this.selectedInterval === Interval.SELECTED_PERIOD ? this.endDate : now;
        const endTime: string =
            this.selectedInterval === Interval.SELECTED_PERIOD
                ? this.endTime
                : `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`;

        this.buildTrack(new Date(this.startDate), this.startTime, new Date(endDate), endTime);
    }

    buildTrack(startDate: Date|number, startTime: string, endDate: Date|number, endTime: string): void {
        if (!this.selectedDevice || this.tableDataLoading) {
            return;
        }

        const createDate = (date: Date|number, time: string): number => {
            let hours: string, minutes: string, seconds: string;
            [ hours, minutes, seconds ] = time.split(':');
            return new Date(date).setHours(Number.parseInt(hours), Number.parseInt(minutes), Number.parseInt(seconds ?? '0'));
        };

        const fromDate = createDate(startDate, startTime);
        const toDate = createDate(endDate, endTime);

        this.trackLoadError = false;
        this.tableDataLoading = true;
        DeviceRepository
            .getPacketsForDevice(
                this.selectedDevice as number,
                fromDate,
                this.selectedInterval === Interval.SELECTED_PERIOD ? toDate : new Date()
            )
            .then(response => {
                this.deviceHistory = response;
                const compileParams = (data: any): string => {
                    const result = [];
                    for (const prop in data) {
                        if (!Object.prototype.hasOwnProperty.call(data, prop)) {
                            continue;
                        }

                        if (this.paramDictionary.exclude.findIndex((q: string) => q === prop) >= 0) {
                            continue;
                        }
                        const displayName =
                            this.paramDictionary.translate.find((q: any) => q.source === prop)?.target
                            ?? prop.toLocaleLowerCase();

                        const value = data[prop];
                        result.push(`${displayName}=${value}`);
                    }
                    return result.join(', ');
                };


                const topLeftCorner = new LatLng(Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER);
                const bottomRightCorner = new LatLng(Number.MIN_SAFE_INTEGER, Number.MIN_SAFE_INTEGER);
                let anyData: boolean = false;

                this.devicePath = this.deviceHistory?.records?.filter(q =>
                    hasValidCoordinates(q.coordinates)
                )
                    .map(item => {
                        const lat = Number(item.coordinates.latitude);
                        const lng = Number(item.coordinates.longitude);
                        topLeftCorner.lat = Math.min(lat, topLeftCorner.lat);
                        topLeftCorner.lng = Math.min(lng, topLeftCorner.lng);
                        bottomRightCorner.lat = Math.max(lat, bottomRightCorner.lat);
                        bottomRightCorner.lng = Math.max(lng, bottomRightCorner.lng);
                        anyData = true;

                        return new LatLng(lat, lng);
                    }) ?? []

                this.map.bounds = anyData ? new LatLngBounds(topLeftCorner, bottomRightCorner) : null;

                let count = 0;
                this.currentPage = 1;
                this.tableData = (this.deviceHistory?.records ?? []).map(item => {
                    const result: any = {
                        '#': ++count,
                        date: item.date,
                        speed: item.coordinates?.speed ?? '',
                        altitude: item.coordinates?.height ?? '',
                        params: compileParams(item.parameterList),
                    };

                    if (hasValidCoordinates(item.coordinates)) {
                        result.location = `${item.coordinates.latitude}, ${item.coordinates.longitude} (${item.coordinates.satellites})`;
                    }

                    return result;
                });
            })
            .catch(() => { this.trackLoadError = true; })
            .finally(() => { this.tableDataLoading = false; });
    }

    updateDeviceList(): void {
        this.deviceLoading = true;
        const that = this;
        DeviceGroupRepository
            .getAllDevicesWithGroups(this.$store)
            .then(response => {
                this.deviceLoading = false;
                this.deviceList = response;
                if (this.deviceList?.length) {
                    let device: IDeviceDto|undefined;
                    if (that.$route.params.deviceId) {
                        const needleId = Number.parseInt(that.$route.params.deviceId);
                        device = that.deviceList.find(q => q.id === needleId);
                    }
                    this.selectedDevice = (device ?? this.deviceList[0]).id;
                }
            });
    }

    loadData() {
        this.updateDeviceMessages();
    }

    finishDateEnabled(): boolean {
        return this.selectedInterval !== Interval.FROM_DATE_TO_NOW;
    }

    resetForm(): void {
        this.selectedInterval = Interval.SELECTED_PERIOD;
        this.startDate = new Date();
        this.startTime = '00:00';
        this.endDate = new Date();
        this.endTime = '23:59';
    }

    formatValue(value: any): string {
        return value ?? '--';
    }

    anyTableData(): boolean {
        return !!this.selectedDevice && !this.tableDataLoading && !this.tableData.length;
    }

    mounted() {
        this.updateDeviceList();
    }
}

export default Messages;
</script>

<style lang="scss">
    @import "../../../custom1";

    .full-height {
        height: calc(100vh - 64px);
    }

    .messages-component {

        .toolbox {
            .inline-form {
                display: flex;
                flex-direction: column;

                .inline-form-group {
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    margin-bottom: 1em;

                    label {
                        min-width: 25%;
                        margin-right: 0.5em;
                    }

                    .inline-form-input {
                        flex-grow: 1;
                        display: flex;

                        * {
                            margin-right: 0.5em;

                            &:last-child {
                                margin-right: 0;
                            }
                        }


                        .b-calendar-grid-body {
                            span {
                                color: white !important;

                                &:hover {
                                    color: $dark!important;
                                }
                            }
                        }

                        .dropdown-menu {
                            background-color: $dark;
                        }
                    }
                }
            }

            .header-bar {
                background-color: lighten($dark, 15%);
                padding: 0.25em;
                text-align: center;
                margin: 0 -1em 0 -1em;
            }

            ul {
                list-style: none;

                li {
                    padding: 0;
                    margin: 0;
                }
            }
        }

        .data-container {
            padding: 1em 0 0 0;
            position: relative;
            min-height: 10em;

            .table-toolbar {
                display: flex;
                justify-content: flex-end;
                align-items: center;

                .form-group {
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    align-items: center;

                    label {
                        margin: 0 1em 0 0;
                    }
                }

                .per-page-select {
                    width: 6em;
                }

            }

            #data-table {
                td {
                    white-space: nowrap;
                }
            }

            .messages-table {
                width: 100%;
                overflow: auto;
            }
        }

    }

</style>